import { type ReactElement, useEffect, useMemo, useState } from 'react';
import { toolbarControlRadius } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { EntityTagsField } from '@/features/shared/components/tags/EntityTagsField';
import { getFormLabel } from '@/shared/copy/forms';
import { useTagOptions } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { withStatusFilterOptionIcons } from '@/ui/composites/filters/statusFilterOptionIcons';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import { TextField } from '@/ui/primitives';
import { DetailEditTimingFields } from './DetailEditDurationFields';
import {
  areDurationPartsEqual,
  toDurationMsFromParts,
  toDurationParts,
  toTimestampDayjs,
  type DurationInputMode,
  type DurationParts,
} from './DetailEditDurationFields.utils';
import { defaultTagColor, normalizeTagsAndColors } from './EditableTagsSection.utils';
import { editDialogInputBackgroundColor, EntityEditDialog } from './EntityEditDialog';
import type { TagColor } from '@/shared/data/tags';

export type { DurationInputMode } from './DetailEditDurationFields.utils';

type TimestampDraftValue = ReturnType<typeof toTimestampDayjs>;

const areStringArraysEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const areTagColorArraysEqual = (left: TagColor[], right: TagColor[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

export type DetailEditEntityDialogProps<TStatus extends string> = {
  open: boolean;
  entityLabel: string;
  entityName: string;
  entityRunNumber?: number | null;
  nameValue?: string;
  nameFallbackValue?: string;
  nameLabel?: string;
  projectId?: string | null;
  tags?: string[];
  status?: TStatus;
  statusOptions?: Array<{ value: TStatus; label: string }>;
  timestamp?: Date | null;
  secondaryTimestamp?: Date | null;
  durationMs?: number | null;
  timestampLabel?: string;
  secondaryTimestampLabel?: string;
  durationLabel?: string;
  durationInputMode?: DurationInputMode;
  loading?: boolean;
  cascadeCopy?: string;
  onClose: () => void;
  onSave?: (input: {
    name?: string;
    tags: string[];
    colors: TagColor[];
    status?: TStatus;
    timestamp?: Date | null;
    secondaryTimestamp?: Date | null;
    durationMs?: number | null;
  }) => Promise<void> | void;
};

export const DetailEditEntityDialog = <TStatus extends string>({
  open,
  entityLabel,
  entityName,
  entityRunNumber,
  nameValue,
  nameFallbackValue,
  nameLabel = 'Name',
  projectId,
  tags,
  status,
  statusOptions,
  timestamp,
  secondaryTimestamp,
  durationMs,
  timestampLabel = 'Timestamp',
  secondaryTimestampLabel = 'Finished',
  durationLabel = 'Duration (ms)',
  durationInputMode = 'milliseconds',
  loading = false,
  cascadeCopy,
  onClose,
  onSave,
}: DetailEditEntityDialogProps<TStatus>): ReactElement | null => {
  const initialNameValue = nameValue ?? nameFallbackValue ?? '';
  const [draftName, setDraftName] = useState(initialNameValue);
  const [draftTags, setDraftTags] = useState<string[]>([]);
  const [draftTagColors, setDraftTagColors] = useState<TagColor[]>([]);
  const [draftStatus, setDraftStatus] = useState<TStatus | null>(status ?? null);
  const [draftTimestampValue, setDraftTimestampValue] = useState<TimestampDraftValue>(
    toTimestampDayjs(timestamp),
  );
  const [draftSecondaryTimestampValue, setDraftSecondaryTimestampValue] =
    useState<TimestampDraftValue>(toTimestampDayjs(secondaryTimestamp));
  const [draftDurationValue, setDraftDurationValue] = useState<string>(
    durationMs !== null && durationMs !== undefined ? String(durationMs) : '',
  );
  const [draftDurationParts, setDraftDurationParts] = useState<DurationParts>(
    toDurationParts(durationMs),
  );
  const showNameSection = nameValue !== undefined || nameFallbackValue !== undefined;
  const showTagsSection = tags !== undefined;
  const showStatusSection = status !== undefined && statusOptions !== undefined;
  const showTimingSection =
    timestamp !== undefined || secondaryTimestamp !== undefined || durationMs !== undefined;
  const resolvedTags = tags ?? [];
  const { tagOptions } = useTagOptions({ projectId, enabled: open && showTagsSection });
  const statusOptionsWithIcons = useMemo(
    () => withStatusFilterOptionIcons(statusOptions ?? []),
    [statusOptions],
  );

  const initialTagColors = useMemo(
    () =>
      resolvedTags.map((tag) => {
        const matchingOption = tagOptions.find((option) => option.value === tag);
        return matchingOption?.color ?? defaultTagColor;
      }),
    [resolvedTags, tagOptions],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    if (showTagsSection) {
      setDraftTags((currentValue) =>
        areStringArraysEqual(currentValue, resolvedTags) ? currentValue : resolvedTags,
      );
      setDraftTagColors((currentValue) =>
        areTagColorArraysEqual(currentValue, initialTagColors) ? currentValue : initialTagColors,
      );
    }
    if (showStatusSection) {
      setDraftStatus((currentValue) => (currentValue === status ? currentValue : status));
    }
    if (showNameSection) {
      setDraftName((currentValue) =>
        currentValue === initialNameValue ? currentValue : initialNameValue,
      );
    }
    if (showTimingSection) {
      const nextTimestampValue = toTimestampDayjs(timestamp);
      const nextSecondaryTimestampValue = toTimestampDayjs(secondaryTimestamp);
      const nextDurationValue =
        durationMs !== null && durationMs !== undefined ? String(durationMs) : '';
      const nextDurationParts = toDurationParts(durationMs);

      setDraftTimestampValue((currentValue) =>
        currentValue?.valueOf() === nextTimestampValue?.valueOf()
          ? currentValue
          : nextTimestampValue,
      );
      setDraftSecondaryTimestampValue((currentValue) =>
        currentValue?.valueOf() === nextSecondaryTimestampValue?.valueOf()
          ? currentValue
          : nextSecondaryTimestampValue,
      );
      setDraftDurationValue((currentValue) =>
        currentValue === nextDurationValue ? currentValue : nextDurationValue,
      );
      setDraftDurationParts((currentValue) =>
        areDurationPartsEqual(currentValue, nextDurationParts) ? currentValue : nextDurationParts,
      );
    }
  }, [
    durationMs,
    initialTagColors,
    initialNameValue,
    open,
    resolvedTags,
    showNameSection,
    showStatusSection,
    showTagsSection,
    showTimingSection,
    status,
    secondaryTimestamp,
    timestamp,
  ]);

  if (!onSave) {
    return null;
  }

  const normalizedInitial = normalizeTagsAndColors(resolvedTags, initialTagColors);
  const normalizedDraft = normalizeTagsAndColors(draftTags, draftTagColors);
  const initialTimestampValue = toTimestampDayjs(timestamp);
  const initialSecondaryTimestampValue = toTimestampDayjs(secondaryTimestamp);
  const normalizedDraftDuration =
    durationInputMode === 'timeParts'
      ? toDurationMsFromParts(draftDurationParts)
      : draftDurationValue.trim().length === 0
        ? null
        : Number(draftDurationValue);
  const normalizedInitialDuration = durationMs ?? null;
  const hasTagChanges = showTagsSection
    ? normalizedDraft.tags.length !== normalizedInitial.tags.length ||
      normalizedDraft.colors.length !== normalizedInitial.colors.length ||
      normalizedDraft.tags.some((tag, index) => tag !== normalizedInitial.tags[index]) ||
      normalizedDraft.colors.some((color, index) => color !== normalizedInitial.colors[index])
    : false;
  const hasNameChanges = showNameSection ? draftName !== initialNameValue : false;
  const hasStatusChanges = showStatusSection ? draftStatus !== status : false;
  const hasTimingChanges = showTimingSection
    ? draftTimestampValue?.valueOf() !== initialTimestampValue?.valueOf() ||
      draftSecondaryTimestampValue?.valueOf() !== initialSecondaryTimestampValue?.valueOf() ||
      normalizedDraftDuration !== normalizedInitialDuration
    : false;
  const hasInvalidDuration =
    normalizedDraftDuration === 'invalid' ||
    (durationInputMode === 'milliseconds' &&
      draftDurationValue.trim().length > 0 &&
      (!Number.isFinite(normalizedDraftDuration) || (normalizedDraftDuration ?? 0) < 0));
  const hasChanges = hasNameChanges || hasTagChanges || hasStatusChanges || hasTimingChanges;

  return (
    <EntityEditDialog
      open={open}
      entityLabel={entityLabel}
      entityName={entityName}
      entityRunNumber={entityRunNumber}
      disableSave={!hasChanges || hasInvalidDuration}
      saving={loading}
      onClose={() => {
        if (loading) {
          return;
        }
        onClose();
      }}
      onSave={async () => {
        await onSave({
          name:
            showNameSection && (nameValue !== undefined || hasNameChanges) ? draftName : undefined,
          tags: showTagsSection ? normalizedDraft.tags : [],
          colors: showTagsSection ? normalizedDraft.colors : [],
          status: showStatusSection ? (draftStatus ?? undefined) : undefined,
          timestamp: showTimingSection ? (draftTimestampValue?.toDate() ?? null) : undefined,
          secondaryTimestamp: showTimingSection
            ? (draftSecondaryTimestampValue?.toDate() ?? null)
            : undefined,
          durationMs:
            showTimingSection && normalizedDraftDuration !== 'invalid'
              ? normalizedDraftDuration
              : undefined,
        });
        onClose();
      }}
    >
      {showNameSection ? (
        <TextField
          label={nameLabel}
          value={draftName}
          onChange={(event) => {
            setDraftName(event.target.value);
          }}
          fieldStyle="settings"
          inputBackgroundColor={editDialogInputBackgroundColor}
          fullWidth
        />
      ) : null}
      {showStatusSection ? (
        <TagsFilterField
          label={getFormLabel('status')}
          value={draftStatus ? [draftStatus] : []}
          options={statusOptionsWithIcons}
          multiple={false}
          showClearButton={false}
          labelPlacement="stacked"
          emptyLabel={getFormLabel('status')}
          controlWidthPx={180}
          controlHeight={36}
          controlRadius={toolbarControlRadius}
          highlightActiveValue={false}
          inputLeftPadding={s('xs')}
          inputBackgroundColor={editDialogInputBackgroundColor}
          dropdownBackgroundColor={editDialogInputBackgroundColor}
          onChange={(nextValues) => {
            setDraftStatus((nextValues[0] as TStatus | undefined) ?? null);
          }}
        />
      ) : null}
      {showTimingSection ? (
        <DetailEditTimingFields
          timestampLabel={timestampLabel}
          timestampValue={draftTimestampValue}
          onTimestampChange={setDraftTimestampValue}
          showSecondaryTimestamp={secondaryTimestamp !== undefined}
          secondaryTimestampLabel={secondaryTimestampLabel}
          secondaryTimestampValue={draftSecondaryTimestampValue}
          onSecondaryTimestampChange={setDraftSecondaryTimestampValue}
          durationInputMode={durationInputMode}
          durationLabel={durationLabel}
          durationValue={draftDurationValue}
          onDurationValueChange={setDraftDurationValue}
          durationParts={draftDurationParts}
          onDurationPartsChange={setDraftDurationParts}
          hasInvalidDuration={hasInvalidDuration}
        />
      ) : null}
      {showTagsSection ? (
        <EntityTagsField
          tags={draftTags}
          colors={draftTagColors}
          helperText={cascadeCopy}
          tagOptions={tagOptions}
          onTagsChange={setDraftTags}
          onColorsChange={setDraftTagColors}
        />
      ) : null}
    </EntityEditDialog>
  );
};
